import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  IconButton,
  CircularProgress,
  Alert,
  AlertTitle,
  TablePagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  Tooltip,
  Stack,
  Grid,
  Collapse,
  Avatar,
  InputAdornment,
  Chip,
} from '@mui/material';
import {
  CheckCircle,
  Cancel,
  Assignment,
  NotificationsActive,
  ExpandMore,
  ExpandLess,
  Refresh,
  Search,
  Clear,
  HourglassTop,
  CheckCircleOutline,
  HighlightOff,
  Person,
  Inventory,
  Close,
} from '@mui/icons-material';
import axios from 'axios';
import Swal from 'sweetalert2';
import { getRequisitions, updateRequisition, getProductStock, bulkUpdateRequisitions } from '../BaseURL';

// ── Theme tokens (mirrors Home.js) ─────────────────────────────────────────
const PRIMARY      = '#41a386';
const PRIMARY_DARK = '#2f8068';
const PRIMARY_BG   = 'rgba(65,163,134,0.10)';
const BORDER       = '#e5e9ef';
const MUTED        = '#64748b';
const STRONG       = '#0f172a';
const LABEL_SX = {
  fontSize: '0.66rem', fontWeight: 700, color: '#94a3b8',
  textTransform: 'uppercase', letterSpacing: '0.6px',
};

// Status palette
const STATUS = {
  pending:  { color: '#d97706', tint: 'rgba(217,119,6,0.10)',  strong: '#92400E', label: 'Pending',  Icon: HourglassTop      },
  approved: { color: '#10b981', tint: 'rgba(16,185,129,0.10)', strong: '#15803D', label: 'Approved', Icon: CheckCircleOutline },
  rejected: { color: '#dc2626', tint: 'rgba(220,38,38,0.10)',  strong: '#991B1B', label: 'Rejected', Icon: HighlightOff      },
};

// Deterministic colour for a requester avatar
const REQUESTER_COLORS = [
  { bg: 'rgba(65,163,134,0.12)',  fg: '#15803D' },
  { bg: 'rgba(14,165,233,0.12)',  fg: '#0369A1' },
  { bg: 'rgba(139,92,246,0.12)',  fg: '#6D28D9' },
  { bg: 'rgba(217,119,6,0.12)',   fg: '#92400E' },
  { bg: 'rgba(236,72,153,0.12)',  fg: '#BE185D' },
  { bg: 'rgba(20,184,166,0.12)',  fg: '#0F766E' },
];
const colourFor = (name = '') => {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) >>> 0;
  return REQUESTER_COLORS[h % REQUESTER_COLORS.length];
};
const initialsOf = (name = '') => {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '?';
  return (parts[0][0] + (parts[1]?.[0] || '')).toUpperCase();
};

// ── Status pill component ──────────────────────────────────────────────────
const StatusPill = ({ status }) => {
  const s = STATUS[status] || STATUS.pending;
  const Lv = s.Icon;
  return (
    <Box sx={{
      display: 'inline-flex', alignItems: 'center', gap: 0.5,
      px: 1, py: 0.25, borderRadius: 1,
      bgcolor: s.tint, color: s.strong,
      fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.4px',
    }}>
      <Lv sx={{ fontSize: 12 }} />
      {s.label}
    </Box>
  );
};

const ManageRequisitions = () => {
  const [requisitions, setRequisitions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Group-level pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Bulk selection state
  const [selectedRequisitions, setSelectedRequisitions] = useState(new Set());
  const [bulkActionDialog, setBulkActionDialog] = useState({
    open: false, action: '', reason: '', submitting: false,
  });

  // Filters
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [requestedByFilter, setRequestedByFilter] = useState('');
  const [approvedByFilter, setApprovedByFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('pending');
  const [searchTerm, setSearchTerm] = useState('');

  // Expanded groups (by requester name)
  const [expandedGroups, setExpandedGroups] = useState(new Set());

  // Action dialogs
  const [actionDialog, setActionDialog] = useState({
    open: false, requisition: null, action: '', quantity: '', reason: '',
    currentStock: 0, loadingStock: false, submitting: false,
  });
  const [processingIds, setProcessingIds] = useState(new Set());

  // Pending approvals popup
  const [pendingApprovalsDialog, setPendingApprovalsDialog] = useState({
    open: false, items: [],
  });

  // Inline approval-quantity edits   (reqId  -> string typed by approver)
  const [approvalQty, setApprovalQty] = useState(new Map());
  // Stock cache per product           (name   -> { loading, value })
  const [stockCache, setStockCache] = useState(new Map());
  // Bulk-approve confirmation dialog
  const [bulkApproveConfirm, setBulkApproveConfirm] = useState({ open: false, rows: [], submitting: false });

  const userInfo = JSON.parse(localStorage.getItem('userInfo')) || {};

  // Tell anyone interested (e.g. the procurement sidebar badge) that pending
  // requisitions have changed — so the bell count refreshes immediately
  // instead of waiting for the next 60 s poll.
  const broadcastRequisitionsChanged = () => {
    try { window.dispatchEvent(new CustomEvent('totalcare:requisitions-changed')); } catch {}
  };

  // ── Loaders ─────────────────────────────────────────────────────────────
  useEffect(() => { loadRequisitions(); /* eslint-disable-next-line */ }, [statusFilter, dateFrom, dateTo, requestedByFilter, approvedByFilter]);
  useEffect(() => { loadPendingApprovals(); }, []);
  useEffect(() => { setSelectedRequisitions(new Set()); setPage(0); }, [statusFilter, dateFrom, dateTo, requestedByFilter, approvedByFilter, searchTerm]);

  const loadRequisitions = async () => {
    try {
      setLoading(true);
      const params = {
        status: statusFilter,
        dateFrom: dateFrom || undefined,
        dateTo: dateTo || undefined,
        requestedBy: requestedByFilter || undefined,
        approvedBy: approvedByFilter || undefined,
      };
      const response = await axios.get(getRequisitions, { params });
      if (response.data.success) setRequisitions(response.data.results);
    } catch (err) {
      console.error(err);
      setError('Failed to load requisitions');
    } finally {
      setLoading(false);
    }
  };

  const loadPendingApprovals = async () => {
    try {
      const response = await axios.get(getRequisitions, { params: { status: 'pending' } });
      if (response.data.success && response.data.results) {
        const pending = response.data.results.filter(r => r.status === 'pending');
        if (pending.length > 0) setPendingApprovalsDialog({ open: true, items: pending });
      }
    } catch (err) { console.error(err); }
  };

  // ── Derived data ────────────────────────────────────────────────────────
  const distinctRequestedBy = useMemo(
    () => [...new Set(requisitions.map(r => r.requestedBy).filter(Boolean))].sort(),
    [requisitions]
  );
  const distinctApprovedBy = useMemo(
    () => [...new Set(requisitions.map(r => r.approvedBy).filter(Boolean))].sort(),
    [requisitions]
  );

  // Apply free-text search on top of server filters
  const visibleRequisitions = useMemo(() => {
    if (!searchTerm.trim()) return requisitions;
    const q = searchTerm.toLowerCase();
    return requisitions.filter(r =>
      r.productName?.toLowerCase().includes(q) ||
      r.productCode?.toLowerCase().includes(q) ||
      r.requestedBy?.toLowerCase().includes(q)
    );
  }, [requisitions, searchTerm]);

  // Group by requester
  const groupedByRequester = useMemo(() => {
    const map = new Map();
    for (const r of visibleRequisitions) {
      const key = r.requestedBy || 'Unknown';
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(r);
    }
    const out = [];
    for (const [name, items] of map.entries()) {
      const totalUnits  = items.reduce((s, x) => s + parseInt(x.requestedQuantity || 0), 0);
      const pending     = items.filter(x => x.status === 'pending').length;
      const approved    = items.filter(x => x.status === 'approved').length;
      const rejected    = items.filter(x => x.status === 'rejected').length;
      const dates       = items.map(x => x.requestDate).filter(Boolean).sort();
      out.push({
        name, items,
        totalLines: items.length,
        totalUnits, pending, approved, rejected,
        earliestDate: dates[0],
        latestDate:   dates[dates.length - 1],
      });
    }
    // Sort: groups with most pending first, then by latestDate desc
    out.sort((a, b) => (b.pending - a.pending) || (String(b.latestDate || '').localeCompare(String(a.latestDate || ''))));
    return out;
  }, [visibleRequisitions]);

  // High-level counters
  const counters = useMemo(() => {
    const c = { total: requisitions.length, pending: 0, approved: 0, rejected: 0 };
    for (const r of requisitions) {
      if (c[r.status] !== undefined) c[r.status]++;
    }
    return c;
  }, [requisitions]);

  // ── Selection helpers ───────────────────────────────────────────────────
  const handleSelectRequisition = (id) => {
    const next = new Set(selectedRequisitions);
    next.has(id) ? next.delete(id) : next.add(id);
    setSelectedRequisitions(next);
  };

  const groupPendingItems = (group) => group.items.filter(i => i.status === 'pending');
  const groupSelectionState = (group) => {
    const pendings = groupPendingItems(group);
    if (!pendings.length) return 'none';
    const sel = pendings.filter(p => selectedRequisitions.has(p.id)).length;
    if (sel === 0) return 'none';
    if (sel === pendings.length) return 'all';
    return 'some';
  };
  const toggleGroupSelection = (group) => {
    const pendings = groupPendingItems(group);
    const next = new Set(selectedRequisitions);
    const state = groupSelectionState(group);
    if (state === 'all') pendings.forEach(p => next.delete(p.id));
    else                 pendings.forEach(p => next.add(p.id));
    setSelectedRequisitions(next);
  };

  // ── Bulk actions ────────────────────────────────────────────────────────
  const handleBulkActionOpen = (action, ids = null) => {
    if (ids) {
      // Open with a specific id list (e.g. group-level approve)
      const next = new Set(ids);
      setSelectedRequisitions(next);
      setBulkActionDialog({ open: true, action, reason: '', submitting: false });
      return;
    }
    if (selectedRequisitions.size === 0) {
      Swal.fire({ title: 'No Selection', text: 'Please select at least one requisition.', icon: 'warning' });
      return;
    }
    setBulkActionDialog({ open: true, action, reason: '', submitting: false });
  };

  const handleBulkActionClose = () =>
    setBulkActionDialog({ open: false, action: '', reason: '', submitting: false });

  const handleBulkActionSubmit = async () => {
    const { action, reason } = bulkActionDialog;
    if (action === 'reject' && !reason.trim()) {
      Swal.fire({ title: 'Error!', text: 'Please provide a reason for rejection.', icon: 'error' });
      return;
    }
    setBulkActionDialog(p => ({ ...p, submitting: true }));
    try {
      const response = await axios.put(bulkUpdateRequisitions, {
        requisitionIds: Array.from(selectedRequisitions),
        action,
        approvedBy: userInfo.name || 'Unknown',
        reason: action === 'reject' ? reason : null,
      });
      if (response.data.success) {
        const { successful, failed } = response.data.results.details;
        if (failed.length > 0) {
          const failedDetails = failed.map(f => `${f.productName}: ${f.reason}`).join('\n');
          Swal.fire({
            title: 'Partial Success',
            html: `<div style="text-align:left">
              <p><strong>Successful:</strong> ${successful.length}</p>
              <p><strong>Failed:</strong> ${failed.length}</p>
              <hr/>
              <p><strong>Failed items:</strong></p>
              <pre style="font-size:12px;max-height:200px;overflow-y:auto;text-align:left">${failedDetails}</pre>
            </div>`,
            icon: 'warning',
          });
        } else {
          Swal.fire({
            title: 'Success!',
            text: `Successfully ${action === 'approve' ? 'approved' : 'rejected'} ${successful.length} requisition(s).`,
            icon: 'success', timer: 2000, showConfirmButton: false,
          });
        }
        setSelectedRequisitions(new Set());
        loadRequisitions();
        broadcastRequisitionsChanged();
        handleBulkActionClose();
      } else {
        throw new Error(response.data.message || 'Failed to process bulk action');
      }
    } catch (err) {
      console.error(err);
      Swal.fire({ title: 'Error!', text: err.response?.data?.message || 'Failed to process bulk action.', icon: 'error' });
    } finally {
      setBulkActionDialog(p => ({ ...p, submitting: false }));
    }
  };

  // ── Stock cache ─────────────────────────────────────────────────────────
  const fetchCurrentStock = async (productName) => {
    try {
      const response = await axios.get(getProductStock(productName));
      if (response.data.success) return response.data.currentStock;
      return 0;
    } catch (err) { console.error(err); return 0; }
  };

  // Lazily fetch stock for every unique pending product in a group.
  const ensureStockForGroup = async (group) => {
    const products = [...new Set(
      group.items.filter(i => i.status === 'pending').map(i => i.productName).filter(Boolean)
    )];
    const toFetch = products.filter(p => {
      const e = stockCache.get(p);
      return !e || (!e.loading && e.value === undefined);
    });
    if (toFetch.length === 0) return;

    setStockCache(prev => {
      const next = new Map(prev);
      toFetch.forEach(p => next.set(p, { loading: true, value: prev.get(p)?.value }));
      return next;
    });

    const pairs = await Promise.all(toFetch.map(async p => [p, await fetchCurrentStock(p)]));
    setStockCache(prev => {
      const next = new Map(prev);
      pairs.forEach(([p, v]) => next.set(p, { loading: false, value: v }));
      return next;
    });
  };

  // ── Inline approval-quantity helpers ────────────────────────────────────
  const getApprovalQty = (req) => {
    const v = approvalQty.get(req.id);
    return v !== undefined ? v : String(req.requestedQuantity ?? '');
  };

  const setQty = (id, val) => {
    setApprovalQty(prev => {
      const next = new Map(prev);
      next.set(id, val);
      return next;
    });
  };

  // Validate qty against stock — returns { ok, qty, stock, reason }
  const validateRowQty = (req) => {
    const qty = parseInt(getApprovalQty(req));
    const entry = stockCache.get(req.productName);
    const stock = entry?.value ?? 0;
    if (!Number.isFinite(qty) || qty <= 0) {
      return { ok: false, qty, stock, reason: 'Quantity must be greater than zero' };
    }
    if (entry?.loading) {
      return { ok: false, qty, stock, reason: 'Stock still loading…' };
    }
    if (qty > stock) {
      return { ok: false, qty, stock, reason: `Only ${stock} unit${stock === 1 ? '' : 's'} in store` };
    }
    return { ok: true, qty, stock };
  };

  // ── Quick approve (no dialog) — uses inline qty ─────────────────────────
  const handleQuickApprove = async (req) => {
    const v = validateRowQty(req);
    if (!v.ok) {
      Swal.fire({
        icon: 'warning', title: 'Cannot approve', text: v.reason,
        toast: true, position: 'top-end', timer: 3500, showConfirmButton: false,
      });
      return;
    }
    setProcessingIds(prev => new Set(prev).add(req.id));
    try {
      const response = await axios.put(updateRequisition(req.id), {
        status: 'approved',
        quantityReceived: v.qty,
        approvedBy: userInfo.name || 'Unknown',
        reason: null,
      });
      if (response.data.success) {
        Swal.fire({
          icon: 'success',
          title: `Approved ${v.qty} unit${v.qty === 1 ? '' : 's'}`,
          text: req.productName,
          toast: true, position: 'top-end', timer: 1800, showConfirmButton: false,
        });
        // Decrement cached stock so subsequent rows reflect the change.
        setStockCache(prev => {
          const next = new Map(prev);
          const cur = next.get(req.productName);
          if (cur && typeof cur.value === 'number') next.set(req.productName, { ...cur, value: cur.value - v.qty });
          return next;
        });
        setApprovalQty(prev => { const n = new Map(prev); n.delete(req.id); return n; });
        loadRequisitions();
        broadcastRequisitionsChanged();
      } else {
        throw new Error(response.data.message || 'Failed to update requisition');
      }
    } catch (err) {
      console.error(err);
      Swal.fire({ title: 'Error!', text: err.response?.data?.message || 'Failed to update requisition.', icon: 'error' });
    } finally {
      setProcessingIds(prev => { const n = new Set(prev); n.delete(req.id); return n; });
    }
  };

  // ── Bulk-approve confirmation flow (uses inline quantities) ─────────────
  const openBulkApproveConfirm = (ids = null) => {
    const idList = ids ? Array.from(ids) : Array.from(selectedRequisitions);
    if (idList.length === 0) {
      Swal.fire({ title: 'No selection', text: 'Pick at least one requisition first.', icon: 'warning' });
      return;
    }
    const rows = idList
      .map(id => requisitions.find(r => r.id === id))
      .filter(Boolean)
      .filter(r => r.status === 'pending');

    if (rows.length === 0) {
      Swal.fire({ title: 'Nothing to approve', text: 'Selected items are not pending.', icon: 'info' });
      return;
    }
    setBulkApproveConfirm({ open: true, rows, submitting: false });
  };

  const handleBulkApproveSubmit = async () => {
    const { rows } = bulkApproveConfirm;
    // Build the per-id quantities map from the inline edits.
    const quantities = {};
    for (const r of rows) {
      const qty = parseInt(getApprovalQty(r));
      if (Number.isFinite(qty) && qty > 0) quantities[r.id] = qty;
    }
    setBulkApproveConfirm(p => ({ ...p, submitting: true }));
    try {
      const response = await axios.put(bulkUpdateRequisitions, {
        requisitionIds: rows.map(r => r.id),
        action: 'approve',
        approvedBy: userInfo.name || 'Unknown',
        quantities,
      });
      if (response.data.success) {
        const { successful, failed } = response.data.results.details;
        if (failed.length > 0) {
          const failedDetails = failed.map(f => `${f.productName}: ${f.reason}`).join('\n');
          Swal.fire({
            title: 'Partial success',
            html: `<div style="text-align:left">
              <p><strong>Approved:</strong> ${successful.length}</p>
              <p><strong>Failed:</strong> ${failed.length}</p>
              <hr/>
              <pre style="font-size:12px;max-height:220px;overflow:auto;text-align:left">${failedDetails}</pre>
            </div>`,
            icon: 'warning',
          });
        } else {
          Swal.fire({
            title: 'Approved',
            text: `${successful.length} requisition${successful.length === 1 ? '' : 's'} approved.`,
            icon: 'success', timer: 2000, showConfirmButton: false,
          });
        }
        setSelectedRequisitions(new Set());
        setApprovalQty(new Map());
        // Invalidate stock cache so freshly affected products refetch when re-expanded.
        setStockCache(new Map());
        loadRequisitions();
        broadcastRequisitionsChanged();
        setBulkApproveConfirm({ open: false, rows: [], submitting: false });
      } else {
        throw new Error(response.data.message || 'Bulk approve failed');
      }
    } catch (err) {
      console.error(err);
      Swal.fire({ title: 'Error', text: err.response?.data?.message || err.message || 'Bulk approve failed.', icon: 'error' });
      setBulkApproveConfirm(p => ({ ...p, submitting: false }));
    }
  };

  const handleActionDialogOpen = async (requisition, action) => {
    setProcessingIds(prev => new Set(prev).add(requisition.id));
    if (action === 'approve') {
      setActionDialog({
        open: true, requisition, action,
        quantity: requisition.requestedQuantity.toString(),
        reason: '', currentStock: 0, loadingStock: true, submitting: false,
      });
      const currentStock = await fetchCurrentStock(requisition.productName);
      setActionDialog({
        open: true, requisition, action,
        quantity: requisition.requestedQuantity.toString(),
        reason: '', currentStock, loadingStock: false, submitting: false,
      });
    } else {
      setActionDialog({
        open: true, requisition, action, quantity: '', reason: '',
        currentStock: 0, loadingStock: false, submitting: false,
      });
    }
  };

  const handleActionDialogClose = () => {
    if (actionDialog.requisition) {
      setProcessingIds(prev => {
        const next = new Set(prev); next.delete(actionDialog.requisition.id); return next;
      });
    }
    setActionDialog({
      open: false, requisition: null, action: '', quantity: '', reason: '',
      currentStock: 0, loadingStock: false, submitting: false,
    });
  };

  const handleActionSubmit = async () => {
    const { requisition, action, quantity, reason, currentStock } = actionDialog;
    if (action === 'approve' && (!quantity || parseInt(quantity) <= 0)) {
      Swal.fire({ title: 'Error!', text: 'Please enter a valid quantity.', icon: 'error' }); return;
    }
    if (action === 'approve' && parseInt(quantity) > currentStock) {
      Swal.fire({
        title: 'Insufficient Stock!',
        text: `Cannot approve ${quantity} units. Only ${currentStock} units available.`,
        icon: 'warning',
      });
      return;
    }
    if (action === 'reject' && !reason.trim()) {
      Swal.fire({ title: 'Error!', text: 'Please provide a reason for rejection.', icon: 'error' }); return;
    }

    setActionDialog(p => ({ ...p, submitting: true }));
    try {
      const response = await axios.put(updateRequisition(requisition.id), {
        status: action === 'approve' ? 'approved' : 'rejected',
        quantityReceived: action === 'approve' ? parseInt(quantity) : 0,
        approvedBy: userInfo.name || 'Unknown',
        reason: action === 'reject' ? reason : null,
      });
      if (response.data.success) {
        Swal.fire({
          title: 'Success!',
          text: `Requisition ${action === 'approve' ? 'approved' : 'rejected'} successfully.`,
          icon: 'success', timer: 2000, showConfirmButton: false,
        });
        setProcessingIds(prev => { const n = new Set(prev); n.delete(requisition.id); return n; });
        loadRequisitions();
        broadcastRequisitionsChanged();
        handleActionDialogClose();
      } else {
        throw new Error(response.data.message || 'Failed to update requisition');
      }
    } catch (err) {
      console.error(err);
      Swal.fire({ title: 'Error!', text: err.response?.data?.message || 'Failed to update requisition.', icon: 'error' });
      setProcessingIds(prev => { const n = new Set(prev); n.delete(requisition.id); return n; });
    } finally {
      setActionDialog(p => ({ ...p, submitting: false }));
    }
  };

  // ── Filters helper ──────────────────────────────────────────────────────
  const handleClearFilters = () => {
    setDateFrom(''); setDateTo(''); setRequestedByFilter(''); setApprovedByFilter('');
    setStatusFilter('pending'); setSearchTerm('');
  };
  const hasFilters = () =>
    !!searchTerm || !!dateFrom || !!dateTo || !!requestedByFilter || !!approvedByFilter || statusFilter !== 'pending';

  const toggleGroupExpand = (group) => {
    const name = group.name;
    const next = new Set(expandedGroups);
    if (next.has(name)) {
      next.delete(name);
    } else {
      next.add(name);
      ensureStockForGroup(group);
    }
    setExpandedGroups(next);
  };

  // ── Loading / error ─────────────────────────────────────────────────────
  if (loading) return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}>
      <CircularProgress size={48} sx={{ color: PRIMARY }} />
      <Typography sx={{ mt: 2, fontSize: '0.9rem', color: MUTED, fontWeight: 500 }}>Loading requisitions…</Typography>
    </Box>
  );
  if (error) return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh', px: 3 }}>
      <Alert severity="error" sx={{ maxWidth: 500, width: '100%', borderRadius: 2 }}>
        <AlertTitle>Error</AlertTitle>{error}
      </Alert>
    </Box>
  );

  // ── Pagination over groups ──────────────────────────────────────────────
  const groupsOnPage = groupedByRequester.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <Box sx={{ flexGrow: 1, bgcolor: '#f8fafc', minHeight: '100vh', py: 2.5 }}>
      <Container maxWidth="xl">

        {/* Header */}
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'stretch', sm: 'center' }}
          spacing={1.5}
          sx={{ mb: 2 }}
        >
          <Box>
            <Typography sx={{ ...LABEL_SX, color: PRIMARY, mb: 0.25 }}>
              Procurement · Approvals
            </Typography>
            <Typography sx={{ fontSize: '1.4rem', fontWeight: 700, color: STRONG, lineHeight: 1.2 }}>
              Manage Requisitions
            </Typography>
            <Typography sx={{ fontSize: '0.78rem', color: MUTED, mt: 0.25 }}>
              {requisitions.length} requisition{requisitions.length === 1 ? '' : 's'} · grouped by requester ·
              {' '}{groupedByRequester.length} requester{groupedByRequester.length === 1 ? '' : 's'} on this view
            </Typography>
          </Box>
          <Stack direction="row" spacing={1}>
            <Tooltip title="Refresh data" arrow>
              <IconButton
                onClick={loadRequisitions}
                size="small"
                sx={{
                  border: `1px solid ${BORDER}`, bgcolor: '#fff', color: MUTED,
                  '&:hover': { bgcolor: '#f1f5f9', color: STRONG },
                }}
              >
                <Refresh fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        </Stack>

        {/* Stats strip */}
        <Grid container spacing={1.25} sx={{ mb: 2 }}>
          {[
            { label: 'Total Records', value: counters.total,    icon: <Assignment       fontSize="small" />, tint: '#f1f5f9',                color: STRONG               },
            { label: 'Pending',       value: counters.pending,  icon: <HourglassTop     fontSize="small" />, tint: STATUS.pending.tint,      color: STATUS.pending.strong  },
            { label: 'Approved',      value: counters.approved, icon: <CheckCircleOutline fontSize="small" />, tint: STATUS.approved.tint,    color: STATUS.approved.strong },
            { label: 'Rejected',      value: counters.rejected, icon: <HighlightOff     fontSize="small" />, tint: STATUS.rejected.tint,     color: STATUS.rejected.strong },
          ].map(t => (
            <Grid item xs={6} md={3} key={t.label}>
              <Paper elevation={0} sx={{
                p: 1.5, borderRadius: 2, border: `1px solid ${BORDER}`, bgcolor: '#fff',
                display: 'flex', alignItems: 'center', gap: 1.5,
              }}>
                <Box sx={{
                  width: 36, height: 36, borderRadius: 1.5,
                  bgcolor: t.tint, color: t.color,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                }}>
                  {t.icon}
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={LABEL_SX}>{t.label}</Typography>
                  <Typography sx={{ fontSize: '1.35rem', fontWeight: 800, color: STRONG, lineHeight: 1.1, mt: 0.25 }}>
                    {Number(t.value || 0).toLocaleString()}
                  </Typography>
                </Box>
              </Paper>
            </Grid>
          ))}
        </Grid>

        {/* Bulk action toolbar */}
        {selectedRequisitions.size > 0 && (
          <Paper elevation={0} sx={{
            mb: 2, px: 1.5, py: 1, borderRadius: 2,
            border: `1px solid ${PRIMARY}`, bgcolor: PRIMARY_BG,
            display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap',
          }}>
            <Typography sx={{ fontWeight: 700, color: PRIMARY_DARK, flex: 1, fontSize: '0.86rem' }}>
              {selectedRequisitions.size} requisition{selectedRequisitions.size !== 1 ? 's' : ''} selected
            </Typography>
            <Button
              size="small" variant="contained"
              startIcon={<CheckCircle sx={{ fontSize: 16 }} />}
              onClick={() => openBulkApproveConfirm()}
              sx={{ bgcolor: PRIMARY, '&:hover': { bgcolor: PRIMARY_DARK }, textTransform: 'none', fontWeight: 700, boxShadow: 'none' }}
            >
              Approve all
            </Button>
            <Button
              size="small" variant="contained" color="error"
              startIcon={<Cancel sx={{ fontSize: 16 }} />}
              onClick={() => handleBulkActionOpen('reject')}
              sx={{ textTransform: 'none', fontWeight: 700, boxShadow: 'none' }}
            >
              Reject all
            </Button>
            <Button
              size="small" variant="text"
              onClick={() => setSelectedRequisitions(new Set())}
              sx={{ textTransform: 'none', color: MUTED, fontWeight: 600, '&:hover': { bgcolor: '#e2e8f0' } }}
            >
              Clear selection
            </Button>
          </Paper>
        )}

        {/* Main panel */}
        <Paper elevation={0} sx={{
          border: `1px solid ${BORDER}`, borderRadius: 2, overflow: 'hidden', bgcolor: '#fff',
        }}>
          {/* Filter row */}
          <Box sx={{
            p: 1.5, borderBottom: `1px solid ${BORDER}`,
            display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, bgcolor: '#fff',
          }}>
            <TextField
              size="small"
              placeholder="Search by product, code, or requester…"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              sx={{ minWidth: 240, flex: { xs: '1 1 100%', md: '0 0 280px' } }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search fontSize="small" sx={{ color: '#94a3b8' }} />
                  </InputAdornment>
                ),
              }}
            />
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Status</InputLabel>
              <Select label="Status" value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="approved">Approved</MenuItem>
                <MenuItem value="rejected">Rejected</MenuItem>
                <MenuItem value="all">All status</MenuItem>
              </Select>
            </FormControl>
            <TextField
              size="small" type="date" label="From"
              value={dateFrom} onChange={e => setDateFrom(e.target.value)}
              InputLabelProps={{ shrink: true }} sx={{ minWidth: 140 }}
            />
            <TextField
              size="small" type="date" label="To"
              value={dateTo} onChange={e => setDateTo(e.target.value)}
              InputLabelProps={{ shrink: true }} sx={{ minWidth: 140 }}
            />
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>Requested by</InputLabel>
              <Select label="Requested by" value={requestedByFilter} onChange={e => setRequestedByFilter(e.target.value)}>
                <MenuItem value=""><em>All</em></MenuItem>
                {distinctRequestedBy.map(n => <MenuItem key={n} value={n}>{n}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>Approved by</InputLabel>
              <Select label="Approved by" value={approvedByFilter} onChange={e => setApprovedByFilter(e.target.value)}>
                <MenuItem value=""><em>All</em></MenuItem>
                {distinctApprovedBy.map(n => <MenuItem key={n} value={n}>{n}</MenuItem>)}
              </Select>
            </FormControl>
            {hasFilters() && (
              <Button size="small" onClick={handleClearFilters} startIcon={<Clear sx={{ fontSize: 16 }} />}
                sx={{ textTransform: 'none', color: MUTED, fontSize: '0.78rem', '&:hover': { bgcolor: '#f1f5f9' } }}>
                Clear
              </Button>
            )}
            <Box sx={{ flex: 1 }} />
            <Typography sx={{ fontSize: '0.74rem', color: MUTED }}>
              {visibleRequisitions.length} item{visibleRequisitions.length === 1 ? '' : 's'} · {groupedByRequester.length} group{groupedByRequester.length === 1 ? '' : 's'}
            </Typography>
          </Box>

          {/* Grouped list */}
          {groupsOnPage.length === 0 ? (
            <Box sx={{ p: 5, textAlign: 'center' }}>
              <Assignment sx={{ fontSize: 36, color: '#cbd5e1', mb: 1 }} />
              <Typography sx={{ fontSize: '0.92rem', fontWeight: 700, color: STRONG }}>
                No requisitions found
              </Typography>
              <Typography sx={{ fontSize: '0.78rem', color: MUTED, mt: 0.25 }}>
                {hasFilters() ? 'Try clearing filters or changing the date range.' : 'Nothing has been requested in this view yet.'}
              </Typography>
            </Box>
          ) : (
            groupsOnPage.map((group) => {
              const isOpen = expandedGroups.has(group.name);
              const colour = colourFor(group.name);
              const selState = groupSelectionState(group);
              const pendingItems = groupPendingItems(group);

              return (
                <Box key={group.name} sx={{ borderBottom: `1px solid ${BORDER}` }}>
                  {/* Group header row */}
                  <Box
                    onClick={() => toggleGroupExpand(group)}
                    sx={{
                      display: 'flex', alignItems: 'center', gap: 1.5,
                      px: 1.5, py: 1.25, cursor: 'pointer',
                      bgcolor: isOpen ? '#f8fafc' : '#fff',
                      borderLeft: `3px solid ${group.pending > 0 ? STATUS.pending.color : 'transparent'}`,
                      '&:hover': { bgcolor: '#f8fafc' },
                      transition: 'background-color 0.12s',
                    }}
                  >
                    {/* Group select-all (only when there are pending items) */}
                    {pendingItems.length > 0 && (
                      <Tooltip title={selState === 'all' ? 'Deselect all in this group' : 'Select all pending in this group'} arrow>
                        <Checkbox
                          size="small"
                          checked={selState === 'all'}
                          indeterminate={selState === 'some'}
                          onClick={(e) => e.stopPropagation()}
                          onChange={() => toggleGroupSelection(group)}
                          sx={{
                            color: PRIMARY, p: 0.5,
                            '&.Mui-checked': { color: PRIMARY },
                            '&.MuiCheckbox-indeterminate': { color: PRIMARY },
                          }}
                        />
                      </Tooltip>
                    )}

                    {/* Avatar */}
                    <Avatar sx={{
                      width: 38, height: 38,
                      bgcolor: colour.bg, color: colour.fg,
                      fontSize: '0.85rem', fontWeight: 800,
                    }}>
                      {initialsOf(group.name)}
                    </Avatar>

                    {/* Name + summary */}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={{ fontSize: '0.92rem', fontWeight: 800, color: STRONG, lineHeight: 1.2 }} noWrap>
                        {group.name}
                      </Typography>
                      <Stack direction="row" spacing={1} sx={{ mt: 0.4, alignItems: 'center', flexWrap: 'wrap', rowGap: 0.5 }}>
                        <Typography sx={{ fontSize: '0.74rem', color: MUTED, fontWeight: 600 }}>
                          {group.totalLines} item{group.totalLines === 1 ? '' : 's'} · {group.totalUnits.toLocaleString()} unit{group.totalUnits === 1 ? '' : 's'}
                        </Typography>
                        {group.earliestDate && (
                          <Typography sx={{ fontSize: '0.72rem', color: '#94a3b8' }}>
                            · {group.earliestDate === group.latestDate ? group.earliestDate : `${group.earliestDate} → ${group.latestDate}`}
                          </Typography>
                        )}
                      </Stack>
                    </Box>

                    {/* Status counts */}
                    <Stack direction="row" spacing={0.75} sx={{ display: { xs: 'none', md: 'flex' } }}>
                      {group.pending > 0 && (
                        <Box sx={{
                          px: 0.85, py: 0.25, borderRadius: 0.85,
                          bgcolor: STATUS.pending.tint, color: STATUS.pending.strong,
                          fontSize: '0.7rem', fontWeight: 700,
                        }}>
                          {group.pending} pending
                        </Box>
                      )}
                      {group.approved > 0 && (
                        <Box sx={{
                          px: 0.85, py: 0.25, borderRadius: 0.85,
                          bgcolor: STATUS.approved.tint, color: STATUS.approved.strong,
                          fontSize: '0.7rem', fontWeight: 700,
                        }}>
                          {group.approved} approved
                        </Box>
                      )}
                      {group.rejected > 0 && (
                        <Box sx={{
                          px: 0.85, py: 0.25, borderRadius: 0.85,
                          bgcolor: STATUS.rejected.tint, color: STATUS.rejected.strong,
                          fontSize: '0.7rem', fontWeight: 700,
                        }}>
                          {group.rejected} rejected
                        </Box>
                      )}
                    </Stack>

                    {/* Quick actions when group has pendings */}
                    {pendingItems.length > 0 && (
                      <Stack direction="row" spacing={0.5}>
                        <Tooltip title={`Approve all ${pendingItems.length} pending from ${group.name}`} arrow>
                          <span>
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                // Make sure stock for this group is loaded so the confirm shows correct available qty.
                                ensureStockForGroup(group);
                                openBulkApproveConfirm(pendingItems.map(p => p.id));
                              }}
                              sx={{ color: PRIMARY, '&:hover': { bgcolor: PRIMARY_BG } }}
                            >
                              <CheckCircle sx={{ fontSize: 18 }} />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title={`Reject all ${pendingItems.length} pending from ${group.name}`} arrow>
                          <span>
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleBulkActionOpen('reject', pendingItems.map(p => p.id));
                              }}
                              sx={{ color: '#dc2626', '&:hover': { bgcolor: 'rgba(220,38,38,0.10)' } }}
                            >
                              <Cancel sx={{ fontSize: 18 }} />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Stack>
                    )}

                    <IconButton size="small" sx={{ color: MUTED }}>
                      {isOpen ? <ExpandLess sx={{ fontSize: 20 }} /> : <ExpandMore sx={{ fontSize: 20 }} />}
                    </IconButton>
                  </Box>

                  {/* Expanded item table */}
                  <Collapse in={isOpen} timeout="auto" unmountOnExit>
                    <Box sx={{ bgcolor: '#fff', borderTop: `1px solid ${BORDER}` }}>
                      <Table size="small">
                        <TableHead>
                          <TableRow sx={{ '& th': { bgcolor: '#f8fafc', borderBottom: `1px solid ${BORDER}`, ...LABEL_SX, py: 1 } }}>
                            <TableCell sx={{ width: 44, pl: 8 }}>
                              {pendingItems.length > 0 && (
                                <Tooltip
                                  title={selState === 'all' ? `Deselect all ${pendingItems.length} pending` : `Select all ${pendingItems.length} pending`}
                                  arrow
                                >
                                  <Checkbox
                                    size="small"
                                    checked={selState === 'all'}
                                    indeterminate={selState === 'some'}
                                    onChange={() => toggleGroupSelection(group)}
                                    sx={{
                                      color: PRIMARY, p: 0.5,
                                      '&.Mui-checked': { color: PRIMARY },
                                      '&.MuiCheckbox-indeterminate': { color: PRIMARY },
                                    }}
                                  />
                                </Tooltip>
                              )}
                            </TableCell>
                            <TableCell>Product</TableCell>
                            <TableCell sx={{ width: 110 }}>Date</TableCell>
                            <TableCell align="right" sx={{ width: 80 }}>Qty req.</TableCell>
                            <TableCell align="center" sx={{ width: 170 }}>Qty to approve</TableCell>
                            <TableCell sx={{ width: 140 }}>Requester action</TableCell>
                            <TableCell sx={{ width: 130 }}>Approved by</TableCell>
                            <TableCell align="center" sx={{ width: 110 }}>Status</TableCell>
                            <TableCell align="right" sx={{ width: 100 }}>Actions</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {group.items.map((req) => {
                            const isPending = req.status === 'pending';
                            const checked = selectedRequisitions.has(req.id);
                            return (
                              <TableRow
                                key={req.id}
                                hover
                                sx={{
                                  bgcolor: checked ? PRIMARY_BG : 'transparent',
                                  borderBottom: `1px solid ${BORDER}`,
                                }}
                              >
                                <TableCell sx={{ pl: 8 }}>
                                  {isPending && (
                                    <Checkbox
                                      size="small"
                                      checked={checked}
                                      onChange={() => handleSelectRequisition(req.id)}
                                      sx={{
                                        color: PRIMARY, p: 0.5,
                                        '&.Mui-checked': { color: PRIMARY },
                                      }}
                                    />
                                  )}
                                </TableCell>
                                <TableCell>
                                  <Stack direction="row" spacing={0.75} alignItems="center" sx={{ flexWrap: 'wrap' }}>
                                    <Typography sx={{ fontSize: '0.86rem', fontWeight: 700, color: STRONG, lineHeight: 1.25 }}>
                                      {req.productName}
                                    </Typography>
                                    {/* Item-type pill — distinguishes drug vs consumable
                                        requests in the shared inbox. */}
                                    {req.item_type === 'consumable' ? (
                                      <Chip size="small" label="Consumable"
                                        sx={{
                                          height: 18, fontSize: '0.62rem', fontWeight: 700,
                                          bgcolor: 'rgba(217,119,6,0.10)', color: '#92400E',
                                          border: '1px solid rgba(217,119,6,0.30)',
                                        }} />
                                    ) : (
                                      <Chip size="small" label="Drug"
                                        sx={{
                                          height: 18, fontSize: '0.62rem', fontWeight: 700,
                                          bgcolor: 'rgba(2,132,199,0.10)', color: '#0369A1',
                                          border: '1px solid rgba(2,132,199,0.30)',
                                        }} />
                                    )}
                                  </Stack>
                                  {req.productCode && (
                                    <Typography sx={{ fontSize: '0.7rem', color: MUTED, mt: 0.15 }}>
                                      {req.item_type === 'consumable' ? `ID: ${req.productCode}` : `Code: ${req.productCode}`}
                                    </Typography>
                                  )}
                                  {req.remarks && (
                                    <Typography sx={{ fontSize: '0.72rem', color: '#94a3b8', mt: 0.25, fontStyle: 'italic' }}>
                                      “{req.remarks}”
                                    </Typography>
                                  )}
                                </TableCell>
                                <TableCell sx={{ color: MUTED, fontSize: '0.78rem', fontVariantNumeric: 'tabular-nums' }}>
                                  {req.requestDate}
                                </TableCell>
                                <TableCell align="right" sx={{ fontWeight: 700, color: STRONG, fontVariantNumeric: 'tabular-nums', fontSize: '0.84rem' }}>
                                  {req.requestedQuantity}
                                </TableCell>
                                <TableCell align="center" sx={{ py: 0.5 }}>
                                  {isPending ? (() => {
                                    const stockEntry = stockCache.get(req.productName);
                                    const stockVal   = stockEntry?.value;
                                    const stockLoading = !!stockEntry?.loading;
                                    const qtyStr = getApprovalQty(req);
                                    const qtyNum = parseInt(qtyStr);
                                    const isInvalid = !Number.isFinite(qtyNum) || qtyNum <= 0;
                                    const overStock = !stockLoading && Number.isFinite(stockVal) && qtyNum > stockVal;
                                    const adjusted  = qtyNum !== parseInt(req.requestedQuantity);
                                    return (
                                      <Stack alignItems="center" spacing={0.4}>
                                        <TextField
                                          size="small" type="number"
                                          value={qtyStr}
                                          onChange={(e) => setQty(req.id, e.target.value)}
                                          onClick={(e) => e.stopPropagation()}
                                          onKeyDown={(e) => {
                                            if (e.key === 'Enter') { e.preventDefault(); handleQuickApprove(req); }
                                          }}
                                          inputProps={{ min: 1, style: { textAlign: 'center', fontWeight: 700, padding: '4px 6px' } }}
                                          error={isInvalid || overStock}
                                          sx={{
                                            width: 110,
                                            '& .MuiOutlinedInput-root': {
                                              bgcolor: adjusted && !overStock && !isInvalid ? PRIMARY_BG : '#fff',
                                              fontVariantNumeric: 'tabular-nums',
                                            },
                                          }}
                                        />
                                        {stockLoading ? (
                                          <Stack direction="row" alignItems="center" spacing={0.5}>
                                            <CircularProgress size={10} sx={{ color: MUTED }} />
                                            <Typography sx={{ fontSize: '0.65rem', color: MUTED }}>loading stock…</Typography>
                                          </Stack>
                                        ) : (
                                          <Box sx={{
                                            display: 'inline-flex', alignItems: 'center', gap: 0.3,
                                            px: 0.7, py: 0.1, borderRadius: 0.7,
                                            bgcolor: overStock ? STATUS.rejected.tint : ((stockVal ?? 0) === 0 ? STATUS.rejected.tint : STATUS.approved.tint),
                                            color:   overStock ? STATUS.rejected.strong : ((stockVal ?? 0) === 0 ? STATUS.rejected.strong : STATUS.approved.strong),
                                            fontSize: '0.62rem', fontWeight: 700,
                                            textTransform: 'uppercase', letterSpacing: '0.4px',
                                          }}>
                                            {overStock
                                              ? `Over · stock ${stockVal ?? 0}`
                                              : `Stock ${stockVal ?? 0}`}
                                          </Box>
                                        )}
                                      </Stack>
                                    );
                                  })() : (
                                    <Typography sx={{
                                      fontWeight: 700,
                                      color: req.quantityReceived ? PRIMARY_DARK : MUTED,
                                      fontVariantNumeric: 'tabular-nums', fontSize: '0.84rem',
                                    }}>
                                      {req.quantityReceived || 0}
                                    </Typography>
                                  )}
                                </TableCell>
                                <TableCell>
                                  {req.requesterApprovedBy ? (
                                    <Box sx={{
                                      display: 'inline-flex', alignItems: 'center', gap: 0.4,
                                      px: 0.85, py: 0.2, borderRadius: 0.85,
                                      bgcolor: STATUS.approved.tint, color: STATUS.approved.strong,
                                      fontSize: '0.7rem', fontWeight: 700,
                                    }}>
                                      <CheckCircle sx={{ fontSize: 12 }} /> {req.requesterApprovedBy}
                                    </Box>
                                  ) : req.requesterRejectedBy ? (
                                    <Box>
                                      <Box sx={{
                                        display: 'inline-flex', alignItems: 'center', gap: 0.4,
                                        px: 0.85, py: 0.2, borderRadius: 0.85,
                                        bgcolor: STATUS.rejected.tint, color: STATUS.rejected.strong,
                                        fontSize: '0.7rem', fontWeight: 700,
                                      }}>
                                        <Cancel sx={{ fontSize: 12 }} /> {req.requesterRejectedBy}
                                      </Box>
                                      {req.requesterRejectionReason && (
                                        <Typography sx={{ fontSize: '0.7rem', color: STATUS.rejected.strong, mt: 0.3 }}>
                                          Reason: {req.requesterRejectionReason}
                                        </Typography>
                                      )}
                                    </Box>
                                  ) : (
                                    <Box sx={{
                                      display: 'inline-flex',
                                      px: 0.85, py: 0.2, borderRadius: 0.85,
                                      bgcolor: '#f1f5f9', color: MUTED,
                                      fontSize: '0.7rem', fontWeight: 700,
                                    }}>
                                      Awaiting
                                    </Box>
                                  )}
                                </TableCell>
                                <TableCell sx={{ color: STRONG, fontSize: '0.8rem', fontWeight: 600 }}>
                                  {req.approvedBy || '—'}
                                  {req.reason && (
                                    <Typography sx={{ fontSize: '0.7rem', color: MUTED, mt: 0.25 }}>
                                      {req.reason}
                                    </Typography>
                                  )}
                                </TableCell>
                                <TableCell align="center">
                                  <StatusPill status={req.status} />
                                </TableCell>
                                <TableCell align="right">
                                  {processingIds.has(req.id) ? (
                                    <CircularProgress size={14} sx={{ color: PRIMARY }} />
                                  ) : (
                                    <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                                      {req.status !== 'approved' && (
                                        req.status === 'pending' ? (
                                          <Tooltip title={`Approve ${getApprovalQty(req)} unit${getApprovalQty(req) === '1' ? '' : 's'}`} arrow>
                                            <IconButton size="small"
                                              onClick={() => handleQuickApprove(req)}
                                              sx={{ color: PRIMARY, '&:hover': { bgcolor: PRIMARY_BG } }}
                                            >
                                              <CheckCircle sx={{ fontSize: 16 }} />
                                            </IconButton>
                                          </Tooltip>
                                        ) : (
                                          <Tooltip title="Approve (override)" arrow>
                                            <IconButton size="small"
                                              onClick={() => handleActionDialogOpen(req, 'approve')}
                                              sx={{ color: PRIMARY, '&:hover': { bgcolor: PRIMARY_BG } }}
                                            >
                                              <CheckCircle sx={{ fontSize: 16 }} />
                                            </IconButton>
                                          </Tooltip>
                                        )
                                      )}
                                      {req.status !== 'rejected' && (
                                        <Tooltip title="Reject" arrow>
                                          <IconButton size="small"
                                            onClick={() => handleActionDialogOpen(req, 'reject')}
                                            sx={{ color: '#dc2626', '&:hover': { bgcolor: 'rgba(220,38,38,0.10)' } }}
                                          >
                                            <Cancel sx={{ fontSize: 16 }} />
                                          </IconButton>
                                        </Tooltip>
                                      )}
                                    </Stack>
                                  )}
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </Box>
                  </Collapse>
                </Box>
              );
            })
          )}

          {/* Pagination over groups */}
          {groupedByRequester.length > 0 && (
            <TablePagination
              component="div"
              count={groupedByRequester.length}
              page={page}
              onPageChange={(_, p) => setPage(p)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={e => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
              rowsPerPageOptions={[5, 10, 25, 50]}
              labelRowsPerPage="Groups per page:"
              sx={{
                borderTop: `1px solid ${BORDER}`,
                '.MuiTablePagination-toolbar': { minHeight: 44 },
                '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': {
                  fontSize: '0.78rem', color: MUTED,
                },
                '.MuiTablePagination-select': { fontSize: '0.78rem', fontWeight: 700, color: STRONG },
              }}
            />
          )}
        </Paper>
      </Container>

      {/* ── Single action dialog ──────────────────────────────────────────── */}
      <Dialog open={actionDialog.open} onClose={handleActionDialogClose} maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}
      >
        <DialogTitle sx={{
          display: 'flex', alignItems: 'center', gap: 1.5, py: 1.75, px: 2.5,
          bgcolor: actionDialog.action === 'approve' ? PRIMARY : '#dc2626', color: '#fff',
        }}>
          {actionDialog.action === 'approve' ? <CheckCircle sx={{ fontSize: 22 }} /> : <Cancel sx={{ fontSize: 22 }} />}
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontSize: '1.05rem', fontWeight: 700, lineHeight: 1.2 }}>
              {actionDialog.action === 'approve' ? 'Approve Requisition' : 'Reject Requisition'}
            </Typography>
            <Typography sx={{ fontSize: '0.78rem', opacity: 0.9 }}>
              {actionDialog.action === 'approve' ? 'Confirm the quantity to release from store' : 'Provide a reason — the requester will see it'}
            </Typography>
          </Box>
          <IconButton onClick={handleActionDialogClose} disabled={actionDialog.submitting} sx={{ color: '#fff' }} size="small">
            <Close sx={{ fontSize: 20 }} />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers sx={{ p: 2.5, bgcolor: '#f8fafc' }}>
          {actionDialog.requisition && (
            <Paper elevation={0} sx={{ p: 1.75, mb: 2, borderRadius: 2, border: `1px solid ${BORDER}`, bgcolor: '#fff' }}>
              <Stack direction="row" alignItems="center" spacing={1.5}>
                <Avatar sx={{ width: 36, height: 36, ...colourFor(actionDialog.requisition.requestedBy) && {}, bgcolor: colourFor(actionDialog.requisition.requestedBy).bg, color: colourFor(actionDialog.requisition.requestedBy).fg, fontSize: '0.82rem', fontWeight: 800 }}>
                  {initialsOf(actionDialog.requisition.requestedBy)}
                </Avatar>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={LABEL_SX}>Requested by</Typography>
                  <Typography sx={{ fontSize: '0.92rem', fontWeight: 700, color: STRONG, lineHeight: 1.2 }}>
                    {actionDialog.requisition.requestedBy}
                  </Typography>
                </Box>
                <Box sx={{ textAlign: 'right' }}>
                  <Typography sx={LABEL_SX}>Requested</Typography>
                  <Typography sx={{ fontSize: '0.92rem', fontWeight: 800, color: STRONG, lineHeight: 1.2, fontVariantNumeric: 'tabular-nums' }}>
                    {actionDialog.requisition.requestedQuantity}
                  </Typography>
                </Box>
              </Stack>
              <Box sx={{ mt: 1.25, pt: 1.25, borderTop: `1px solid ${BORDER}` }}>
                <Typography sx={LABEL_SX}>Product</Typography>
                <Typography sx={{ fontSize: '0.92rem', fontWeight: 700, color: PRIMARY }}>
                  {actionDialog.requisition.productName}
                </Typography>
              </Box>
            </Paper>
          )}

          {actionDialog.action === 'approve' ? (
            <>
              <Paper elevation={0} sx={{
                p: 1.5, mb: 1.5, borderRadius: 2,
                border: `1px solid ${BORDER}`, bgcolor: '#fff',
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              }}>
                <Stack direction="row" spacing={1.25} alignItems="center">
                  <Box sx={{
                    width: 32, height: 32, borderRadius: 1.25,
                    bgcolor: PRIMARY_BG, color: PRIMARY,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <Inventory sx={{ fontSize: 18 }} />
                  </Box>
                  <Box>
                    <Typography sx={LABEL_SX}>Current stock in store</Typography>
                    {actionDialog.loadingStock ? (
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <CircularProgress size={14} sx={{ color: PRIMARY }} />
                        <Typography sx={{ fontSize: '0.82rem', color: MUTED }}>Loading…</Typography>
                      </Stack>
                    ) : (
                      <Typography sx={{
                        fontSize: '1.05rem', fontWeight: 800,
                        color: actionDialog.currentStock > 0 ? PRIMARY_DARK : '#dc2626',
                        lineHeight: 1.1, fontVariantNumeric: 'tabular-nums',
                      }}>
                        {actionDialog.currentStock} units
                      </Typography>
                    )}
                  </Box>
                </Stack>
                {!actionDialog.loadingStock &&
                  actionDialog.requisition &&
                  actionDialog.currentStock < actionDialog.requisition.requestedQuantity && (
                  <Box sx={{
                    px: 1, py: 0.4, borderRadius: 1,
                    bgcolor: STATUS.pending.tint, color: STATUS.pending.strong,
                    fontSize: '0.7rem', fontWeight: 700,
                  }}>
                    Insufficient
                  </Box>
                )}
              </Paper>
              <TextField
                fullWidth size="small"
                label="Quantity to approve"
                type="number"
                value={actionDialog.quantity}
                onChange={(e) => setActionDialog(p => ({ ...p, quantity: e.target.value }))}
                inputProps={{
                  min: 1,
                  max: Math.min(actionDialog.requisition?.requestedQuantity || 999, actionDialog.currentStock || 999),
                }}
                helperText={
                  actionDialog.loadingStock
                    ? 'Loading stock information…'
                    : `Maximum: ${Math.min(actionDialog.requisition?.requestedQuantity || 0, actionDialog.currentStock || 0)} (Available: ${actionDialog.currentStock})`
                }
                error={parseInt(actionDialog.quantity) > actionDialog.currentStock && !actionDialog.loadingStock}
                disabled={actionDialog.loadingStock}
                required
              />
            </>
          ) : (
            <TextField
              fullWidth size="small"
              label="Reason for rejection"
              multiline rows={3}
              value={actionDialog.reason}
              onChange={(e) => setActionDialog(p => ({ ...p, reason: e.target.value }))}
              placeholder="Explain why this requisition is being rejected…"
              required
            />
          )}
        </DialogContent>

        <DialogActions sx={{ p: 1.5, bgcolor: '#fff', borderTop: `1px solid ${BORDER}` }}>
          <Button onClick={handleActionDialogClose} disabled={actionDialog.submitting}
            sx={{ textTransform: 'none', color: MUTED, fontWeight: 600, '&:hover': { bgcolor: '#f1f5f9' } }}>
            Cancel
          </Button>
          <Button
            onClick={handleActionSubmit}
            variant="contained"
            disabled={actionDialog.loadingStock || actionDialog.submitting}
            startIcon={actionDialog.submitting
              ? <CircularProgress size={16} sx={{ color: 'inherit' }} />
              : (actionDialog.action === 'approve' ? <CheckCircle sx={{ fontSize: 18 }} /> : <Cancel sx={{ fontSize: 18 }} />)}
            sx={{
              bgcolor: actionDialog.action === 'approve' ? PRIMARY : '#dc2626',
              '&:hover': { bgcolor: actionDialog.action === 'approve' ? PRIMARY_DARK : '#b91c1c' },
              textTransform: 'none', fontWeight: 700, px: 3, boxShadow: 'none',
            }}
          >
            {actionDialog.submitting
              ? (actionDialog.action === 'approve' ? 'Approving…' : 'Rejecting…')
              : (actionDialog.action === 'approve' ? 'Approve' : 'Reject')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Bulk action dialog ────────────────────────────────────────────── */}
      <Dialog open={bulkActionDialog.open} onClose={handleBulkActionClose} maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}
      >
        <DialogTitle sx={{
          display: 'flex', alignItems: 'center', gap: 1.5, py: 1.75, px: 2.5,
          bgcolor: bulkActionDialog.action === 'approve' ? PRIMARY : '#dc2626', color: '#fff',
        }}>
          {bulkActionDialog.action === 'approve' ? <CheckCircle sx={{ fontSize: 22 }} /> : <Cancel sx={{ fontSize: 22 }} />}
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontSize: '1.05rem', fontWeight: 700, lineHeight: 1.2 }}>
              Bulk {bulkActionDialog.action === 'approve' ? 'Approve' : 'Reject'} Requisitions
            </Typography>
            <Typography sx={{ fontSize: '0.78rem', opacity: 0.9 }}>
              {selectedRequisitions.size} item{selectedRequisitions.size !== 1 ? 's' : ''} selected
            </Typography>
          </Box>
          <IconButton onClick={handleBulkActionClose} disabled={bulkActionDialog.submitting} sx={{ color: '#fff' }} size="small">
            <Close sx={{ fontSize: 20 }} />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers sx={{ p: 2.5, bgcolor: '#f8fafc' }}>
          <Alert severity="info" sx={{ mb: bulkActionDialog.action === 'reject' ? 2 : 0, borderRadius: 1.5 }}>
            <AlertTitle sx={{ fontWeight: 700, fontSize: '0.86rem' }}>
              You're about to {bulkActionDialog.action} {selectedRequisitions.size} requisition{selectedRequisitions.size !== 1 ? 's' : ''}
            </AlertTitle>
            {bulkActionDialog.action === 'approve'
              ? 'Each item is checked for stock availability. Items without enough stock will be skipped.'
              : 'This will reject all selected requisitions in one go.'}
          </Alert>
          {bulkActionDialog.action === 'reject' && (
            <TextField
              fullWidth size="small"
              label="Reason for rejection"
              multiline rows={3}
              value={bulkActionDialog.reason}
              onChange={(e) => setBulkActionDialog(p => ({ ...p, reason: e.target.value }))}
              placeholder="Explain why these are being rejected…"
              required
            />
          )}
        </DialogContent>

        <DialogActions sx={{ p: 1.5, bgcolor: '#fff', borderTop: `1px solid ${BORDER}` }}>
          <Button onClick={handleBulkActionClose} disabled={bulkActionDialog.submitting}
            sx={{ textTransform: 'none', color: MUTED, fontWeight: 600, '&:hover': { bgcolor: '#f1f5f9' } }}>
            Cancel
          </Button>
          <Button
            onClick={handleBulkActionSubmit}
            variant="contained"
            disabled={bulkActionDialog.submitting}
            startIcon={bulkActionDialog.submitting
              ? <CircularProgress size={16} sx={{ color: 'inherit' }} />
              : (bulkActionDialog.action === 'approve' ? <CheckCircle sx={{ fontSize: 18 }} /> : <Cancel sx={{ fontSize: 18 }} />)}
            sx={{
              bgcolor: bulkActionDialog.action === 'approve' ? PRIMARY : '#dc2626',
              '&:hover': { bgcolor: bulkActionDialog.action === 'approve' ? PRIMARY_DARK : '#b91c1c' },
              textTransform: 'none', fontWeight: 700, px: 3, boxShadow: 'none',
            }}
          >
            {bulkActionDialog.submitting
              ? 'Processing…'
              : (bulkActionDialog.action === 'approve' ? 'Approve all' : 'Reject all')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Bulk approve confirmation (with per-row qty review) ──────────── */}
      <Dialog
        open={bulkApproveConfirm.open}
        onClose={() => !bulkApproveConfirm.submitting && setBulkApproveConfirm({ open: false, rows: [], submitting: false })}
        maxWidth="md" fullWidth
        PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}
      >
        <DialogTitle sx={{
          display: 'flex', alignItems: 'center', gap: 1.5, py: 1.75, px: 2.5,
          bgcolor: PRIMARY, color: '#fff',
        }}>
          <CheckCircle sx={{ fontSize: 22 }} />
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontSize: '1.05rem', fontWeight: 700, lineHeight: 1.2 }}>
              Confirm Bulk Approval
            </Typography>
            <Typography sx={{ fontSize: '0.78rem', opacity: 0.9 }}>
              {bulkApproveConfirm.rows.length} requisition{bulkApproveConfirm.rows.length !== 1 ? 's' : ''} · review quantities before approving
            </Typography>
          </Box>
          <IconButton
            onClick={() => setBulkApproveConfirm({ open: false, rows: [], submitting: false })}
            disabled={bulkApproveConfirm.submitting}
            sx={{ color: '#fff' }} size="small"
          >
            <Close sx={{ fontSize: 20 }} />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers sx={{ p: 0, bgcolor: '#f8fafc' }}>
          <Box sx={{ px: 2, pt: 1.5, pb: 1 }}>
            <Alert severity="info" sx={{ borderRadius: 1.5 }}>
              <AlertTitle sx={{ fontWeight: 700, fontSize: '0.86rem' }}>
                Quantities can still be edited here
              </AlertTitle>
              Items with insufficient stock will be skipped automatically by the server.
            </Alert>
          </Box>
          <Box sx={{ px: 2, pb: 2 }}>
            <Paper elevation={0} sx={{ borderRadius: 2, border: `1px solid ${BORDER}`, bgcolor: '#fff', overflow: 'hidden' }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ '& th': { bgcolor: '#f8fafc', borderBottom: `1px solid ${BORDER}`, ...LABEL_SX, py: 1 } }}>
                    <TableCell>Product</TableCell>
                    <TableCell>Requester</TableCell>
                    <TableCell align="right" sx={{ width: 70 }}>Req.</TableCell>
                    <TableCell align="center" sx={{ width: 130 }}>Approving</TableCell>
                    <TableCell align="center" sx={{ width: 100 }}>Stock</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {bulkApproveConfirm.rows.map(req => {
                    const stockEntry = stockCache.get(req.productName);
                    const stockVal   = stockEntry?.value;
                    const stockLoading = !!stockEntry?.loading;
                    const qtyStr = getApprovalQty(req);
                    const qtyNum = parseInt(qtyStr);
                    const isInvalid = !Number.isFinite(qtyNum) || qtyNum <= 0;
                    const overStock = !stockLoading && Number.isFinite(stockVal) && qtyNum > stockVal;
                    return (
                      <TableRow key={req.id} sx={{ borderBottom: `1px solid ${BORDER}` }}>
                        <TableCell>
                          <Typography sx={{ fontSize: '0.84rem', fontWeight: 700, color: STRONG }}>
                            {req.productName}
                          </Typography>
                          {req.productCode && (
                            <Typography sx={{ fontSize: '0.7rem', color: MUTED }}>{req.productCode}</Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <Avatar sx={{
                              width: 22, height: 22,
                              bgcolor: colourFor(req.requestedBy).bg,
                              color:   colourFor(req.requestedBy).fg,
                              fontSize: '0.65rem', fontWeight: 800,
                            }}>
                              {initialsOf(req.requestedBy)}
                            </Avatar>
                            <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: STRONG }}>
                              {req.requestedBy}
                            </Typography>
                          </Stack>
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700, color: STRONG, fontVariantNumeric: 'tabular-nums' }}>
                          {req.requestedQuantity}
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5 }}>
                          <TextField
                            size="small" type="number"
                            value={qtyStr}
                            onChange={(e) => setQty(req.id, e.target.value)}
                            inputProps={{ min: 1, style: { textAlign: 'center', fontWeight: 700, padding: '4px 6px' } }}
                            error={isInvalid || overStock}
                            sx={{
                              width: 100,
                              '& .MuiOutlinedInput-root': {
                                bgcolor: !overStock && !isInvalid && qtyNum !== parseInt(req.requestedQuantity) ? PRIMARY_BG : '#fff',
                                fontVariantNumeric: 'tabular-nums',
                              },
                            }}
                          />
                        </TableCell>
                        <TableCell align="center">
                          {stockLoading ? (
                            <CircularProgress size={12} sx={{ color: MUTED }} />
                          ) : (
                            <Box sx={{
                              display: 'inline-flex',
                              px: 0.7, py: 0.15, borderRadius: 0.7,
                              bgcolor: overStock ? STATUS.rejected.tint : ((stockVal ?? 0) === 0 ? STATUS.rejected.tint : STATUS.approved.tint),
                              color:   overStock ? STATUS.rejected.strong : ((stockVal ?? 0) === 0 ? STATUS.rejected.strong : STATUS.approved.strong),
                              fontSize: '0.66rem', fontWeight: 700,
                            }}>
                              {stockVal ?? '—'}
                            </Box>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Paper>
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 1.5, bgcolor: '#fff', borderTop: `1px solid ${BORDER}`, justifyContent: 'space-between' }}>
          <Stack direction="row" spacing={2} sx={{ pl: 1 }}>
            <Box>
              <Typography sx={LABEL_SX}>Lines</Typography>
              <Typography sx={{ fontSize: '1rem', fontWeight: 800, color: STRONG, lineHeight: 1.1 }}>
                {bulkApproveConfirm.rows.length}
              </Typography>
            </Box>
            <Box>
              <Typography sx={LABEL_SX}>Total Units</Typography>
              <Typography sx={{ fontSize: '1rem', fontWeight: 800, color: PRIMARY, lineHeight: 1.1, fontVariantNumeric: 'tabular-nums' }}>
                {bulkApproveConfirm.rows.reduce((s, r) => {
                  const q = parseInt(getApprovalQty(r));
                  return s + (Number.isFinite(q) && q > 0 ? q : 0);
                }, 0).toLocaleString()}
              </Typography>
            </Box>
          </Stack>
          <Stack direction="row" spacing={1}>
            <Button
              onClick={() => setBulkApproveConfirm({ open: false, rows: [], submitting: false })}
              disabled={bulkApproveConfirm.submitting}
              sx={{ textTransform: 'none', color: MUTED, fontWeight: 600, '&:hover': { bgcolor: '#f1f5f9' } }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleBulkApproveSubmit}
              variant="contained"
              disabled={bulkApproveConfirm.submitting}
              startIcon={bulkApproveConfirm.submitting
                ? <CircularProgress size={16} sx={{ color: 'inherit' }} />
                : <CheckCircle sx={{ fontSize: 18 }} />}
              sx={{ bgcolor: PRIMARY, '&:hover': { bgcolor: PRIMARY_DARK }, textTransform: 'none', fontWeight: 700, px: 3, boxShadow: 'none' }}
            >
              {bulkApproveConfirm.submitting
                ? 'Approving…'
                : `Approve ${bulkApproveConfirm.rows.length}`}
            </Button>
          </Stack>
        </DialogActions>
      </Dialog>

      {/* ── Pending Approvals popup (grouped) ─────────────────────────────── */}
      <Dialog
        open={pendingApprovalsDialog.open}
        onClose={() => setPendingApprovalsDialog({ open: false, items: [] })}
        maxWidth="md" fullWidth
        PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}
      >
        <DialogTitle sx={{
          display: 'flex', alignItems: 'center', gap: 1.5, py: 1.75, px: 2.5,
          bgcolor: PRIMARY, color: '#fff',
        }}>
          <NotificationsActive sx={{ fontSize: 22 }} />
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontSize: '1.05rem', fontWeight: 700, lineHeight: 1.2 }}>
              Pending Approvals
            </Typography>
            <Typography sx={{ fontSize: '0.78rem', opacity: 0.9 }}>
              {pendingApprovalsDialog.items.length} requisition{pendingApprovalsDialog.items.length !== 1 ? 's' : ''} waiting · grouped by requester
            </Typography>
          </Box>
          <IconButton onClick={() => setPendingApprovalsDialog({ open: false, items: [] })} sx={{ color: '#fff' }} size="small">
            <Close sx={{ fontSize: 20 }} />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers sx={{ p: 2.5, bgcolor: '#f8fafc' }}>
          {(() => {
            // Group pending items by requester for the popup
            const m = new Map();
            for (const it of pendingApprovalsDialog.items) {
              const k = it.requestedBy || 'Unknown';
              if (!m.has(k)) m.set(k, []);
              m.get(k).push(it);
            }
            const groups = [...m.entries()].map(([name, items]) => ({
              name, items,
              units: items.reduce((s, x) => s + parseInt(x.requestedQuantity || 0), 0),
            })).sort((a, b) => b.items.length - a.items.length);

            return (
              <Stack spacing={1}>
                {groups.map((g) => {
                  const colour = colourFor(g.name);
                  return (
                    <Paper
                      key={g.name}
                      elevation={0}
                      sx={{ borderRadius: 2, border: `1px solid ${BORDER}`, bgcolor: '#fff', overflow: 'hidden' }}
                    >
                      <Box sx={{
                        display: 'flex', alignItems: 'center', gap: 1.5,
                        px: 1.5, py: 1.25,
                        bgcolor: '#fff',
                        borderLeft: `3px solid ${STATUS.pending.color}`,
                      }}>
                        <Avatar sx={{
                          width: 36, height: 36,
                          bgcolor: colour.bg, color: colour.fg,
                          fontSize: '0.82rem', fontWeight: 800,
                        }}>
                          {initialsOf(g.name)}
                        </Avatar>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography sx={{ fontSize: '0.92rem', fontWeight: 800, color: STRONG, lineHeight: 1.2 }} noWrap>
                            {g.name}
                          </Typography>
                          <Typography sx={{ fontSize: '0.74rem', color: MUTED, fontWeight: 600, mt: 0.25 }}>
                            {g.items.length} item{g.items.length === 1 ? '' : 's'} · {g.units.toLocaleString()} unit{g.units === 1 ? '' : 's'} pending
                          </Typography>
                        </Box>
                        <Box sx={{
                          px: 0.85, py: 0.25, borderRadius: 0.85,
                          bgcolor: STATUS.pending.tint, color: STATUS.pending.strong,
                          fontSize: '0.7rem', fontWeight: 700,
                        }}>
                          {g.items.length} pending
                        </Box>
                      </Box>
                      <Box sx={{ px: 1.5, py: 1.25, borderTop: `1px solid ${BORDER}`, bgcolor: '#f8fafc' }}>
                        <Stack spacing={0.75}>
                          {g.items.slice(0, 5).map(it => (
                            <Stack key={it.id} direction="row" spacing={1} alignItems="center">
                              <Box component="span" sx={{ color: STATUS.pending.color, fontWeight: 800 }}>•</Box>
                              <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: STRONG, flex: 1 }} noWrap>
                                {it.productName}
                              </Typography>
                              <Typography sx={{ fontSize: '0.78rem', color: MUTED, fontVariantNumeric: 'tabular-nums' }}>
                                {it.requestedQuantity} units
                              </Typography>
                              <Typography sx={{ fontSize: '0.72rem', color: '#94a3b8' }}>
                                {it.requestDate}
                              </Typography>
                            </Stack>
                          ))}
                          {g.items.length > 5 && (
                            <Typography sx={{ fontSize: '0.74rem', color: MUTED, fontStyle: 'italic', pl: 2 }}>
                              + {g.items.length - 5} more…
                            </Typography>
                          )}
                        </Stack>
                      </Box>
                    </Paper>
                  );
                })}
              </Stack>
            );
          })()}
        </DialogContent>

        <DialogActions sx={{ p: 1.5, bgcolor: '#fff', borderTop: `1px solid ${BORDER}` }}>
          <Button onClick={() => setPendingApprovalsDialog({ open: false, items: [] })}
            variant="contained"
            startIcon={<Person sx={{ fontSize: 18 }} />}
            sx={{ bgcolor: PRIMARY, '&:hover': { bgcolor: PRIMARY_DARK }, textTransform: 'none', fontWeight: 700, px: 3, boxShadow: 'none' }}
          >
            Review now
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ManageRequisitions;
